import { useEffect, useState, useMemo } from "react";
import AppShell from "@/components/AppShell";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCurrencyRail } from "@/context/CurrencyRailContext";
import { formatCurrency } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, TrendingUp, TrendingDown, LineChart, Activity, Receipt, ChevronDown, ChevronUp, Zap, Sparkles } from "lucide-react";
import ConsolidatedRailCard from "@/components/ConsolidatedRailCard";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { toast } from "sonner";
import { KIND_LABEL, KIND_ICON } from "@/components/investments/shared";
import { StatMini } from "@/components/investments/StatMini";
import { ActivityPanel } from "@/components/investments/ActivityPanel";
import { TaxReportDialog } from "@/components/investments/TaxReportDialog";

export default function Investments() {
  const { user } = useAuth();
  const currency = user?.currency || "USD";
  const { activeRail, isActive, filterRails } = useCurrencyRail();
  const [data, setData] = useState({ positions: [], totals: { value: 0, cost: 0, gain: 0, gain_pct: 0 }, by_currency: [] });
  const [netWorth, setNetWorth] = useState(null);
  const [history, setHistory] = useState([]);
  const [open, setOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [activityFor, setActivityFor] = useState(null);   // the position we're logging activities against
  const [taxReportOpen, setTaxReportOpen] = useState(false);
  const [expandedInterestId, setExpandedInterestId] = useState(null); // MMF interest-log expand
  const [accruing, setAccruing] = useState(false);
  const [form, setForm] = useState({
    symbol: "", name: "", kind: "stock", quantity: "", cost_basis: "",
    coingecko_id: "", currency, notes: "",
  });
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: port }, { data: nw }, { data: hist }] = await Promise.all([
        api.get("/investments/portfolio"),
        api.get("/investments/net-worth?snapshot=true"),
        api.get("/investments/net-worth/history?days=90"),
      ]);
      setData(port);
      setNetWorth(nw);
      setHistory(hist);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    try {
      // For MMF and Cash positions, the fund is valued at NAV = 1.0, so
      // quantity and cost_basis should equal the user-entered "balance".
      // Otherwise the P&L would show a huge phantom loss (balance − 1).
      const balanceKind = form.kind === "money_market" || form.kind === "cash";
      const qty = balanceKind ? Number(form.cost_basis) : Number(form.quantity);
      const cost = Number(form.cost_basis);
      await api.post("/investments", {
        ...form,
        quantity: qty,
        cost_basis: cost,
        coingecko_id: form.kind === "crypto" ? (form.coingecko_id || form.symbol.toLowerCase()) : null,
      });
      toast.success("Investment added");
      setOpen(false);
      setForm({ symbol: "", name: "", kind: "stock", quantity: "", cost_basis: "", coingecko_id: "", currency, notes: "" });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not add");
    }
  };

  const remove = async () => {
    await api.delete(`/investments/${deleteId}`);
    toast.success("Deleted");
    setDeleteId(null);
    load();
  };

  // Iter 65 — trigger manual interest accrual for MMF/cash positions so
  // users can "catch up" mid-month without waiting for the background loop.
  const accrueNow = async (posId) => {
    setAccruing(true);
    try {
      const { data } = await api.post(`/investments/${posId}/accrue`);
      if (data.skipped) {
        toast.info(data.skipped);
      } else if (data.activity) {
        toast.success(`Accrued ${formatCurrency(data.activity.amount || 0, data.activity.currency)}`);
      } else {
        toast.success("Accrual complete");
      }
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Accrual failed");
    } finally {
      setAccruing(false);
    }
  };

  // Rail-isolated derived data (iter 60h) — filter positions and totals by
  // the active sidebar rail so USD and NGN never mix.
  const visibleRails = useMemo(
    () => filterRails(data.by_currency || []),
    [data.by_currency, filterRails]
  );
  const visiblePositions = useMemo(
    () => data.positions.filter((p) => isActive(p.currency || "USD")),
    [data.positions, isActive]
  );

  return (
    <AppShell>
      <div className="flex items-end justify-between mb-8 flex-wrap gap-3">
        <div>
          <div className="text-xs tracking-[0.2em] uppercase text-[var(--muted-text)]">Portfolio</div>
          <h1 className="font-display text-4xl mt-2">Investments</h1>
          <p className="text-sm text-[var(--muted-text)] mt-1">
            Track stocks, crypto and cash. Live prices from Yahoo Finance & CoinGecko.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button data-testid="tax-report-btn" variant="outline" className="h-11"
                  onClick={() => setTaxReportOpen(true)}>
            <Receipt className="w-4 h-4 mr-2" /> Tax report
          </Button>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-investment-btn" className="rounded-full bg-[#E07A5F] hover:bg-[#c9694f] text-white h-11 px-5">
              <Plus className="w-4 h-4 mr-2" /> Add holding
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader><DialogTitle>Add investment</DialogTitle></DialogHeader>
            <form onSubmit={create} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Kind</Label>
                  <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
                    <SelectTrigger data-testid="inv-kind"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(KIND_LABEL).map(([k, v]) => (
                        <SelectItem key={k} value={k}>{v}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Symbol / Ticker</Label>
                  <Input
                    data-testid="inv-symbol" required
                    value={form.symbol}
                    onChange={(e) => setForm({ ...form, symbol: e.target.value })}
                    placeholder={form.kind === "crypto" ? "BTC" : "AAPL"}
                  />
                </div>
              </div>
              {form.kind === "crypto" && (
                <div className="space-y-2">
                  <Label>CoinGecko ID (optional)</Label>
                  <Input
                    data-testid="inv-coingecko"
                    value={form.coingecko_id}
                    onChange={(e) => setForm({ ...form, coingecko_id: e.target.value })}
                    placeholder="bitcoin, ethereum, solana..."
                  />
                  <p className="text-xs text-[var(--muted-text)]">
                    Defaults to symbol in lowercase. Check coingecko.com if the price doesn&apos;t load.
                  </p>
                </div>
              )}
              <div className="space-y-2">
                <Label>Name (optional)</Label>
                <Input
                  data-testid="inv-name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g. Apple Inc, Bitcoin"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                {(form.kind === "money_market" || form.kind === "cash") ? (
                  <div className="space-y-2 col-span-2">
                    <Label>Fund balance</Label>
                    <Input
                      data-testid="inv-cost"
                      type="number" step="0.01" min="0" required
                      value={form.cost_basis}
                      onChange={(e) => setForm({ ...form, cost_basis: e.target.value })}
                      placeholder={form.kind === "money_market" ? "e.g. 1000000 for ₦1M in the fund" : "current cash on hand"}
                    />
                    <p className="text-xs text-[var(--muted-text)]">
                      {form.kind === "money_market"
                        ? "The current balance of the MMF at NAV = 1.00. Interest accrues automatically each month if you set a yield %."
                        : "Cash equivalent — value = balance, no market price applies."}
                    </p>
                  </div>
                ) : (
                  <>
                    <div className="space-y-2">
                      <Label>Quantity</Label>
                      <Input
                        data-testid="inv-quantity"
                        type="number" step="any" min="0" required
                        value={form.quantity}
                        onChange={(e) => setForm({ ...form, quantity: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Cost basis (total)</Label>
                      <Input
                        data-testid="inv-cost"
                        type="number" step="0.01" min="0" required
                        value={form.cost_basis}
                        onChange={(e) => setForm({ ...form, cost_basis: e.target.value })}
                      />
                    </div>
                  </>
                )}
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button type="button" variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
                <Button
                  data-testid="inv-submit" type="submit"
                  disabled={
                    !form.symbol ||
                    ((form.kind === "money_market" || form.kind === "cash")
                      ? !form.cost_basis || Number(form.cost_basis) <= 0
                      : !form.quantity)
                  }
                  className="rounded-full bg-[#2F4F4F] hover:bg-[#213838] text-[#FDFCF8]"
                >
                  Add
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* iter 72t — Net worth is rendered PER CURRENCY RAIL (never converted
          or summed across currencies). USD stays USD, NGN stays NGN. The
          top strip stacks one row per rail; each row shows the rail's
          Net Worth, Liquid cash, Investments, and Card Debt (if any). */}
      {netWorth && (netWorth.by_currency?.length ? (
        <div data-testid="net-worth-strip" className="space-y-3 mb-6">
          {netWorth.by_currency.map((r) => (
            <div key={r.currency}
                 data-testid={`net-worth-rail-${r.currency}`}
                 className="grid grid-cols-2 md:grid-cols-5 gap-3 items-stretch">
              <div className="col-span-2 md:col-span-1 flex items-center gap-2 px-3 py-2 rounded-xl border border-[var(--divider)] bg-[var(--card)]">
                <span className="text-[10px] tracking-[0.2em] uppercase font-semibold">{r.currency}</span>
                <span className="text-[11px] text-[var(--muted-text)]">rail</span>
              </div>
              <StatMini label="Net worth" value={formatCurrency(r.net_worth, r.currency)}
                        accent="brand" testid={`stat-net-${r.currency}`} />
              <StatMini label="Liquid" value={formatCurrency(r.liquid, r.currency)}
                        testid={`stat-liquid-${r.currency}`} />
              <StatMini label="Investments" value={formatCurrency(r.investments, r.currency)}
                        testid={`stat-investments-${r.currency}`} />
              <StatMini label={r.card_debt > 0 ? "Card debt" : "Accounts"}
                        value={formatCurrency(r.card_debt > 0 ? r.card_debt : r.accounts_balance, r.currency)}
                        testid={`stat-${r.card_debt > 0 ? "card-debt" : "accounts"}-${r.currency}`} />
            </div>
          ))}
        </div>
      ) : (
        <div data-testid="net-worth-strip" className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
          <StatMini label="Net worth" value={formatCurrency(netWorth.net_worth, netWorth.currency || currency)} accent="brand" testid="stat-net" />
          <StatMini label="Liquid" value={formatCurrency(netWorth.liquid, netWorth.currency || currency)} testid="stat-liquid" />
          <StatMini label="Accounts" value={formatCurrency(netWorth.accounts_balance, netWorth.currency || currency)} testid="stat-accounts" />
          <StatMini label="Investments" value={formatCurrency(netWorth.investments, netWorth.currency || currency)} testid="stat-investments" />
          <StatMini label="Card debt" value={formatCurrency(netWorth.card_debt || 0, netWorth.currency || currency)} testid="stat-card-debt" />
        </div>
      ))}

      {history.length > 1 && (() => {
        // iter 72u — reshape snapshots into a per-currency chart. Each
        // point becomes {date, USD, NGN, ...} so Recharts can render one
        // line per rail. Fallback: if a snapshot lacks by_currency, use
        // its scalar net_worth under the snapshot's stored currency.
        const ccyOrder = [];
        const points = history.map((h) => {
          const p = { date: h.date };
          const rows = Array.isArray(h.by_currency) && h.by_currency.length
            ? h.by_currency
            : [{ currency: h.currency || "USD", net_worth: h.net_worth || 0 }];
          for (const r of rows) {
            const c = r.currency || "USD";
            p[c] = Number(r.net_worth || 0);
            if (!ccyOrder.includes(c)) ccyOrder.push(c);
          }
          return p;
        });
        // Sort so home rail (first seen) stays first.
        ccyOrder.sort((a, b) => (a === "USD" ? -1 : b === "USD" ? 1 : a.localeCompare(b)));
        const RAIL_COLOR = { USD: "#1E5A96", NGN: "#2F7A55" };
        const fallbackColors = ["#8B4B18", "#5A2A82", "#B4283A", "#D97706"];
        return (
          <div data-testid="net-worth-chart" className="card-flat p-6 mb-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs tracking-[0.2em] uppercase text-[var(--muted-text)]">Trend</div>
                <h3 className="font-display text-xl mt-1">Net worth over time</h3>
                <p className="text-xs text-[var(--muted-text)] mt-1">One line per currency rail — rails are never converted or summed.</p>
              </div>
              <div className="text-xs text-[var(--muted-text)]">Last {history.length} snapshots</div>
            </div>
            <div className="h-56 mt-4">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={points} margin={{ top: 5, right: 10, bottom: 5, left: 0 }}>
                  <defs>
                    {ccyOrder.map((c, i) => {
                      const color = RAIL_COLOR[c] || fallbackColors[i % fallbackColors.length];
                      return (
                        <linearGradient key={c} id={`nwGrad-${c}`} x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor={color} stopOpacity={0.28} />
                          <stop offset="100%" stopColor={color} stopOpacity={0} />
                        </linearGradient>
                      );
                    })}
                  </defs>
                  <CartesianGrid strokeDasharray="2 4" stroke="var(--border)" vertical={false} />
                  <XAxis dataKey="date" tick={{ fontSize: 11, fill: "var(--muted-text)" }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: "var(--muted-text)" }} axisLine={false} tickLine={false} width={70} />
                  <Tooltip
                    formatter={(v, name) => [formatCurrency(v, name), name]}
                    contentStyle={{ border: "1px solid var(--border)", borderRadius: 8, background: "var(--surface)", color: "var(--text)" }}
                  />
                  {ccyOrder.map((c, i) => {
                    const color = RAIL_COLOR[c] || fallbackColors[i % fallbackColors.length];
                    return (
                      <Area key={c} type="monotone" dataKey={c} name={c}
                            stroke={color} strokeWidth={2.4}
                            fill={`url(#nwGrad-${c})`}
                            connectNulls
                            data-testid={`net-worth-chart-line-${c}`} />
                    );
                  })}
                </AreaChart>
              </ResponsiveContainer>
            </div>
            <div className="flex items-center gap-4 mt-3">
              {ccyOrder.map((c, i) => {
                const color = RAIL_COLOR[c] || fallbackColors[i % fallbackColors.length];
                return (
                  <div key={c} className="flex items-center gap-2 text-xs" data-testid={`net-worth-chart-legend-${c}`}>
                    <span className="w-2.5 h-2.5 rounded-full" style={{ background: color }} />
                    <span className="text-[var(--muted-text)]">{c}</span>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}

      <div data-testid="portfolio-totals" className="space-y-3 mb-4">
        {/* FX-consolidated total across USD + NGN rails — shows only when the
            sidebar "Convert to USD" toggle is ON and 2+ rails have holdings. */}
        <ConsolidatedRailCard
          rails={data.by_currency || []}
          testId="investments-consolidated"
          subtitle="portfolio value + cost + gain across every rail"
          fields={[
            { key: "value", label: "Portfolio value" },
            { key: "cost", label: "Cost basis" },
            { key: "gain", label: "Gain / Loss", color: (data.by_currency || []).reduce((a, r) => a + (r.gain || 0), 0) >= 0 ? "text-[#4C956C]" : "text-[#E63946]" },
          ]}
        />

        {visibleRails.length === 0 && (
          <div className="card-flat p-6 text-center text-sm text-[var(--muted-text)]">
            No holdings on the <b>{activeRail}</b> rail. Add a position or switch the sidebar rail selector.
          </div>
        )}
        {visibleRails.map((r) => (
          <div
            key={`rail-${r.currency}`}
            data-testid={`portfolio-rail-${r.currency}`}
            className="card-flat p-6 flex flex-wrap items-center gap-6 border-l-4"
            style={{ borderLeftColor: r.currency === "USD" ? "#1E5A96" : "#4C956C" }}
          >
            <div className="flex items-center gap-2">
              <span
                className="px-2 py-0.5 rounded text-xs font-mono text-[#FDFCF8]"
                style={{ backgroundColor: r.currency === "USD" ? "#1E5A96" : "#4C956C" }}
              >{r.currency}</span>
              <span className="text-xs text-[var(--muted-text)]">
                {r.positions} position{r.positions === 1 ? "" : "s"}
              </span>
            </div>
            <div>
              <div className="text-[10px] tracking-[0.2em] uppercase text-[var(--muted-text)]">Portfolio value</div>
              <div className="font-mono-data text-2xl font-semibold mt-1" data-testid={`portfolio-value-${r.currency}`}>
                {formatCurrency(r.value, r.currency)}
              </div>
            </div>
            <div>
              <div className="text-[10px] tracking-[0.2em] uppercase text-[var(--muted-text)]">Cost basis</div>
              <div className="font-mono-data text-2xl font-semibold mt-1">{formatCurrency(r.cost, r.currency)}</div>
            </div>
            <div>
              <div className="text-[10px] tracking-[0.2em] uppercase text-[var(--muted-text)]">Gain / Loss</div>
              <div
                className={`font-mono-data text-2xl font-semibold mt-1 ${r.gain >= 0 ? "text-[#4C956C]" : "text-[#E63946]"}`}
                data-testid={`portfolio-gain-${r.currency}`}
              >
                {r.gain >= 0 ? "+" : "−"}{formatCurrency(Math.abs(r.gain), r.currency)}
                <span className="text-sm ml-2">({r.gain_pct.toFixed(2)}%)</span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {visiblePositions.length === 0 && data.positions.length === 0 ? (
        <div data-testid="positions-empty" className="card-flat p-12 text-center">
          <LineChart className="w-8 h-8 mx-auto text-[var(--muted-text)]" />
          <h3 className="font-display text-xl mt-3">No holdings yet</h3>
          <p className="text-sm text-[var(--muted-text)] mt-1">Add stocks or crypto to see live P/L.</p>
        </div>
      ) : (
        <div data-testid="positions-list" className="card-flat overflow-hidden">
          <div className="grid grid-cols-12 px-6 py-3 border-b border-[var(--border)] text-[11px] tracking-[0.2em] uppercase text-[var(--muted-text)]">
            <div className="col-span-3">Asset</div>
            <div className="col-span-2 text-right">Quantity</div>
            <div className="col-span-2 text-right">Price</div>
            <div className="col-span-2 text-right">Value</div>
            <div className="col-span-2 text-right">Gain / Loss</div>
            <div className="col-span-1" />
          </div>
          <div className="divide-y divide-[var(--divider)]">
            {visiblePositions.map((p) => {
              const Icon = KIND_ICON[p.kind] || LineChart;
              const isYielding = (p.kind === "money_market" || p.kind === "cash");
              const hasInterest = isYielding && ((p.interest_ytd || 0) > 0 || (p.interest_recent || []).length > 0);
              const isExpanded = expandedInterestId === p.id;
              return (
                <div key={p.id} data-testid={`position-row-${p.id}`}>
                  <div className="grid grid-cols-12 px-6 py-3 items-center hover:bg-[var(--row-hover)]">
                  <div className="col-span-3 flex items-center gap-3">
                    <div className="w-8 h-8 rounded-md bg-[#2F4F4F] text-[#FDFCF8] flex items-center justify-center">
                      <Icon className="w-4 h-4" />
                    </div>
                    <div>
                      <div className="text-sm font-medium flex items-center gap-2">
                        {p.symbol}
                        {isYielding && p.yield_pct > 0 && (
                          <span data-testid={`position-yield-${p.id}`} className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-[#EDF3EF] text-[#4C956C]">
                            <Sparkles className="w-2.5 h-2.5" /> {p.yield_pct}% APY
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-[var(--muted-text)]">{p.name || KIND_LABEL[p.kind]}</div>
                    </div>
                  </div>
                  <div className="col-span-2 text-right font-mono-data text-sm">{p.quantity}</div>
                  <div className="col-span-2 text-right font-mono-data text-sm">
                    {p.price_available ? formatCurrency(p.current_price, p.currency) : "—"}
                  </div>
                  <div className="col-span-2 text-right font-mono-data text-sm">
                    {formatCurrency(p.current_value, p.currency)}
                    {hasInterest && (
                      <div data-testid={`position-interest-ytd-${p.id}`} className="text-[10px] text-[#4C956C] mt-0.5">
                        +{formatCurrency(p.interest_ytd, p.currency)} interest YTD
                      </div>
                    )}
                  </div>
                  <div className={`col-span-2 text-right font-mono-data text-sm flex items-center justify-end gap-1 ${
                    p.gain >= 0 ? "text-[#4C956C]" : "text-[#E63946]"
                  }`}>
                    {p.gain >= 0 ? <TrendingUp className="w-3.5 h-3.5" /> : <TrendingDown className="w-3.5 h-3.5" />}
                    {p.gain >= 0 ? "+" : "−"}{formatCurrency(Math.abs(p.gain), p.currency)}
                    <span className="text-xs ml-1">({p.gain_pct.toFixed(1)}%)</span>
                  </div>
                  <div className="col-span-1 flex justify-end gap-1">
                    {isYielding && (
                      <Button
                        data-testid={`position-interest-toggle-${p.id}`}
                        size="icon" variant="ghost"
                        title={isExpanded ? "Hide interest log" : "Show interest log"}
                        onClick={() => setExpandedInterestId(isExpanded ? null : p.id)}
                      >
                        {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                      </Button>
                    )}
                    <Button
                      data-testid={`position-activity-${p.id}`}
                      size="icon" variant="ghost"
                      title="Log activity (dividend, buy, sell, etc.)"
                      onClick={() => setActivityFor(p)}
                    >
                      <Activity className="w-4 h-4 text-[var(--brand)]" />
                    </Button>
                    <Button
                      data-testid={`position-delete-${p.id}`}
                      size="icon" variant="ghost"
                      onClick={() => setDeleteId(p.id)}
                    >
                      <Trash2 className="w-4 h-4 text-[#E63946]" />
                    </Button>
                  </div>
                  </div>
                  {/* Interest log — inline expandable strip under MMF/cash rows */}
                  {isYielding && isExpanded && (
                    <div data-testid={`position-interest-log-${p.id}`} className="px-6 pb-5 pt-1 bg-[#FBF9F3]/60 border-t border-[var(--divider)]">
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <div className="text-[10px] tracking-[0.2em] uppercase text-[var(--muted-text)]">Interest log</div>
                          <div className="text-xs text-[var(--muted-text)] mt-1">
                            {(p.interest_recent || []).length > 0
                              ? `${(p.interest_recent || []).length} recent accrual${(p.interest_recent || []).length === 1 ? "" : "s"} · Total earned: ${formatCurrency(p.interest_total || 0, p.currency)}`
                              : "No interest posted yet. Set a yield % on this position and interest accrues monthly."}
                          </div>
                          {p.last_accrued && (
                            <div className="text-[10px] text-[var(--muted-text)] mt-0.5">Last accrual · {p.last_accrued}</div>
                          )}
                        </div>
                        <Button
                          data-testid={`position-accrue-now-${p.id}`}
                          size="sm" variant="outline"
                          disabled={accruing || !p.yield_pct}
                          onClick={() => accrueNow(p.id)}
                          className="h-8 gap-1.5"
                          title={!p.yield_pct ? "Add a yield % to this position first" : "Manually accrue any pending interest through today"}
                        >
                          <Zap className="w-3 h-3" /> Accrue now
                        </Button>
                      </div>
                      {(p.interest_recent || []).length > 0 && (
                        <div className="rounded-md border border-[var(--divider)] overflow-hidden bg-white">
                          <div className="grid grid-cols-12 px-3 py-1.5 text-[10px] uppercase tracking-wider text-[var(--muted-text)] border-b border-[var(--divider)] bg-[var(--hover)]">
                            <div className="col-span-3">Date</div>
                            <div className="col-span-3 text-right">Amount</div>
                            <div className="col-span-6">Note</div>
                          </div>
                          {(p.interest_recent || []).map((a) => (
                            <div key={a.id} data-testid={`interest-row-${a.id}`} className="grid grid-cols-12 px-3 py-1.5 text-xs border-b border-[var(--divider)] last:border-b-0">
                              <div className="col-span-3 font-mono-data">{a.date}</div>
                              <div className="col-span-3 text-right font-mono-data text-[#4C956C]">+{formatCurrency(a.amount, p.currency)}</div>
                              <div className="col-span-6 text-[var(--muted-text)] truncate">{a.notes || "Monthly interest accrual"}</div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      <AlertDialog open={!!deleteId} onOpenChange={(v) => !v && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove holding?</AlertDialogTitle>
            <AlertDialogDescription>The position will be removed from your portfolio.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="inv-delete-confirm"
              onClick={remove}
              className="bg-[#E63946] hover:bg-[#c92e3b] text-white"
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <ActivityPanel
        position={activityFor}
        onClose={() => setActivityFor(null)}
        onChange={() => load()}
      />

      <TaxReportDialog
        open={taxReportOpen}
        onClose={() => setTaxReportOpen(false)}
        currency={currency}
      />
    </AppShell>
  );
}
